import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ErrorTypeEnum } from '../../../../main/constants/error-type.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';

import { updateParentAccountMutationRequest } from '../../../requests/update-parent-account.mutation.ts';
import { ParentAccountUpdateFormSchema } from '../../../types/accounts-validation.types.ts';

import { ACCOUNTS_SLICE_NAME, AccountsSliceState } from '../accounts.slice.types.ts';

interface Props {
  preferredName: string;
}

const THUNK_NAME = `${ACCOUNTS_SLICE_NAME}/updateParentAccount`;

const handleError = (input: Props, message?: string): Error => {
  const errorMessage = message ?? getLocaleUtil().select('accounts:update_parent_account_failed');
  getToasterUtil().error(errorMessage);
  const error = new Error(errorMessage);
  error.name = ErrorTypeEnum.UPDATE_PARENT_ACCOUNT_ERROR;

  getSentryUtil().trackError(
    error,
    {
      thunk: THUNK_NAME,
      errorType: ErrorTypeEnum.UPDATE_PARENT_ACCOUNT_ERROR,
    },
    { input }
  );
  getTrackerUtil().track({
    event: TrackingEventEnum.PARENT_ACCOUNT_UPDATE_FAILED,
  });
  return error;
};

export const updateParentAccountThunk = createAsyncThunk<void, Props, ThunkApiConfigType>(THUNK_NAME, async (props) => {
  const { preferredName } = props;

  const parsedInput = ParentAccountUpdateFormSchema.safeParse(props);
  if (!parsedInput.success) {
    const error = handleError(props);
    throw error;
  }

  const result = await updateParentAccountMutationRequest(parsedInput.data);
  if (!result?.success) {
    const error = handleError(props, result.message);
    throw error;
  }

  getToasterUtil().success(getLocaleUtil().select('accounts:update_parent_account_success'));
  getTrackerUtil().track({
    event: TrackingEventEnum.PARENT_ACCOUNT_UPDATED,
    data: { preferredName },
  });
});

export const updateParentAccountThunkExtraReducers = (builder: ActionReducerMapBuilder<AccountsSliceState>): void => {
  builder.addCase(updateParentAccountThunk.pending, (state) => {
    state.isUpdateAccountLoading = true;
  });

  builder.addCase(updateParentAccountThunk.fulfilled, (state, action) => {
    state.isUpdateAccountLoading = false;
    if (state.currentAccount) {
      state.currentAccount.preferredName = action.meta.arg.preferredName;
    }
  });

  builder.addCase(updateParentAccountThunk.rejected, (state) => {
    state.isUpdateAccountLoading = false;
  });
};
